import { useState } from 'react';

import { ActivateButton } from '../../components/ActivateButton';
import { REGIONS_PER_SIDE, regionsFor, type BodySide } from './notfallData';

/**
 * Where it hurts: a front/back toggle and a grid of body regions.
 *
 * **Not the SVG figure the design asked for**, and the reason is arithmetic rather than taste. In the
 * 708 × 326 px this panel gets, a standing figure renders at scale 0.776, so a 16 mm target would
 * need 107 viewBox units — leaving room for 1.9 horizontal zones. A figure could not have carried a
 * left/right split at all, and its regions would have come out around 4 mm. That is unusable with
 * impaired motor control, and it would have looked entirely plausible on screen. The full working is
 * in `notfallData.ts`.
 *
 * A 3 × 3 grid in the same space gives 38 × 21 mm tiles, repeats the interaction the person already
 * learns on Sätze, and reads — written language is intact.
 *
 * Every region speaks a complete stored sentence, never a composed one: German prepositions do not
 * compose, and this is the panel most likely to be heard by a paramedic.
 */
export function BodyMap({ onSpeak }: { onSpeak: (text: string) => void }): React.JSX.Element {
  const [side, setSide] = useState<BodySide>('front');
  const regions = regionsFor(side);

  return (
    <div style={{ display: 'flex', gap: 'var(--key-gap)', height: '100%', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--key-gap)',
          flex: '0 0 110px',
        }}
      >
        <SideButton side="front" active={side} onPress={setSide} label="Vorne" />
        <SideButton side="back" active={side} onPress={setSide} label="Hinten" />
      </div>

      <div
        data-testid={`body-map:${side}`}
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          // Exactly three rows: nine slots is the whole set, so nothing scrolls and nothing hides
          // below a fold on the surface where hiding something matters most.
          gridTemplateRows: `repeat(${Math.ceil(REGIONS_PER_SIDE / 3)}, 1fr)`,
          gap: 'var(--key-gap)',
        }}
      >
        {regions.map((region) => (
          <ActivateButton
            key={region.id}
            id={`body:${region.id}`}
            onActivate={() => onSpeak(region.utterance)}
            label={region.utterance}
            style={{
              minWidth: 0,
              minHeight: 0,
              fontSize: 'var(--font-button)',
              background: 'var(--key-bg)',
              color: 'var(--key-fg)',
              border: '3px solid var(--key-border)',
              borderRadius: 'var(--radius)',
            }}
          >
            {region.label}
          </ActivateButton>
        ))}
      </div>
    </div>
  );
}

function SideButton({
  side,
  active,
  label,
  onPress,
}: {
  side: BodySide;
  active: BodySide;
  label: string;
  onPress: (side: BodySide) => void;
}): React.JSX.Element {
  const isActive = active === side;
  return (
    <ActivateButton
      id={`body-side:${side}`}
      onActivate={() => onPress(side)}
      pressed={isActive}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        minHeight: 0,
        fontSize: 'var(--font-button)',
        fontWeight: isActive ? 700 : 400,
        background: isActive ? 'var(--fg)' : 'var(--key-bg)',
        color: isActive ? 'var(--bg)' : 'var(--key-fg)',
        border: '3px solid var(--key-border)',
        borderRadius: 'var(--radius)',
      }}
    >
      {label}
    </ActivateButton>
  );
}
